import { ITEM_NAMES } from "@ghostland/shared";
import { itemThumb } from "../items/itemThumb.js";
import { injectTheme } from "./theme.js";

/**
 * The loot-chest popup: a small panel listing the items in a nearby chest. Click an item to take it
 * (it goes to the bag; the server removes it and re-syncs, which calls `updateChest`). Closes when
 * the chest empties, the player walks away, or Esc.
 */

const STYLE_ID = "ghostland-chest-style";
const CSS = `
#gl-chest-overlay {
  position: fixed; left: 50%; bottom: 96px; transform: translateX(-50%); z-index: 28;
  display: none; font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text);
}
#gl-chest-overlay.open { display: block; }
#gl-chest-card {
  padding: 12px 14px 14px; background: var(--gl-panel); border: 1px solid var(--gl-border);
  border-radius: 12px; box-shadow: 0 16px 50px rgba(0,0,0,.7), 0 0 0 1px rgba(255,210,60,.15);
}
#gl-chest-head { display: flex; align-items: baseline; justify-content: space-between; gap: 18px; margin: 0 0 10px; }
#gl-chest-head h1 { margin: 0; font-size: 13px; letter-spacing: .16em; }
#gl-chest-head .hint { font-size: 10px; color: var(--gl-muted); }
#gl-chest-items { display: flex; gap: 10px; }
.gl-chest-item {
  display: flex; flex-direction: column; align-items: center; gap: 4px; cursor: pointer;
  background: var(--gl-panel-2); border: 2px solid var(--gl-border); border-radius: 8px; padding: 6px 8px 5px;
}
.gl-chest-item:hover { border-color: #ffd23f; box-shadow: 0 0 0 2px rgba(255,210,60,.25) inset; }
.gl-chest-item canvas { image-rendering: pixelated; width: 40px; height: 40px; pointer-events: none; }
.gl-chest-item span { font-size: 10px; color: var(--gl-muted); max-width: 64px; text-align: center; }
`;

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS;
  document.head.appendChild(s);
}

let overlay: HTMLDivElement | null = null;
let itemsEl: HTMLDivElement | null = null;
let chestId = "";
let takeCb: ((index: number) => void) | null = null;

function ensureOverlay(): HTMLDivElement {
  if (overlay) return overlay;
  ensureStyle();
  overlay = document.createElement("div");
  overlay.id = "gl-chest-overlay";
  overlay.innerHTML = `
    <div id="gl-chest-card">
      <div id="gl-chest-head"><h1>Loot</h1><span class="hint">click to take · Esc to close</span></div>
      <div id="gl-chest-items"></div>
    </div>`;
  document.body.appendChild(overlay);
  itemsEl = overlay.querySelector<HTMLDivElement>("#gl-chest-items")!;
  return overlay;
}

function render(items: readonly string[]): void {
  if (!itemsEl) return;
  itemsEl.innerHTML = "";
  items.forEach((itemId, index) => {
    const el = document.createElement("div");
    el.className = "gl-chest-item";
    el.appendChild(itemThumb(itemId));
    const label = document.createElement("span");
    label.textContent = ITEM_NAMES[itemId as keyof typeof ITEM_NAMES] ?? itemId;
    el.appendChild(label);
    el.addEventListener("click", () => takeCb?.(index));
    itemsEl!.appendChild(el);
  });
}

/** Open the loot panel for a chest. `onTake(index)` is called when the player clicks an item. */
export function openChest(id: string, items: readonly string[], onTake: (index: number) => void): void {
  ensureOverlay();
  chestId = id; takeCb = onTake;
  render(items);
  overlay!.classList.add("open");
}

/** Refresh the open panel's items (after a take re-syncs); closes when empty or for a different chest. */
export function updateChest(id: string, items: readonly string[]): void {
  if (!isChestOpen() || id !== chestId) return;
  if (items.length === 0) { closeChest(); return; }
  render(items);
}

export function closeChest(): void {
  overlay?.classList.remove("open");
  chestId = ""; takeCb = null;
}

export function isChestOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}

export function currentChestId(): string {
  return isChestOpen() ? chestId : "";
}
